import React, { useEffect, useState, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { useWeb3 } from '../context/Web3Context';
import { Button } from './ui/button';
import { Award, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * TierUpModal — auto-detects when the connected user has unlocked a new
 * NFT-eligible tier (Bronze/Silver/Gold/Diamond) and shows a celebratory
 * full-screen modal with confetti + sound + instant mint CTA.
 *
 * Polls /api/social-sharing/pending-unlock/{address} every 30s.
 * The user can dismiss (acknowledge) or mint right away.
 */
export default function TierUpModal() {
    const { address } = useWeb3();
    const [unlock, setUnlock] = useState(null);
    const [minting, setMinting] = useState(false);
    const [shown, setShown] = useState(false);

    // Programmatic confetti burst
    const fireConfetti = useCallback((color) => {
        const fire = (particleRatio, opts) => {
            confetti(Object.assign({}, opts, {
                origin: { y: 0.6 },
                particleCount: Math.floor(200 * particleRatio),
            }));
        };
        const colors = [color, '#ffffff', '#FFD700'];
        fire(0.25, { spread: 26, startVelocity: 55, colors });
        fire(0.2,  { spread: 60, colors });
        fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8, colors });
        fire(0.1,  { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2, colors });
        fire(0.1,  { spread: 120, startVelocity: 45, colors });
    }, []);

    // Play a synthesized "level up" sound via Web Audio (no external file needed)
    const playLevelUpSound = useCallback(() => {
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            const ctx = new Ctx();
            const notes = [523.25, 659.25, 783.99, 1046.50]; // C5 E5 G5 C6
            notes.forEach((freq, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'triangle';
                osc.frequency.value = freq;
                gain.gain.value = 0;
                osc.connect(gain).connect(ctx.destination);
                const start = ctx.currentTime + i * 0.15;
                gain.gain.linearRampToValueAtTime(0.18, start + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.4);
                osc.start(start);
                osc.stop(start + 0.45);
            });
        } catch (e) { /* silent */ }
    }, []);

    // Poll for pending unlock
    useEffect(() => {
        if (!address) { setUnlock(null); setShown(false); return; }
        let cancelled = false;
        const check = async () => {
            try {
                const r = await fetch(`${API}/api/social-sharing/pending-unlock/${address}`);
                const d = await r.json();
                if (cancelled) return;
                if (d.pending && !shown) {
                    setUnlock(d.pending);
                    setShown(true);
                    setTimeout(() => {
                        fireConfetti(d.pending.tier?.color || '#FFD700');
                        playLevelUpSound();
                    }, 200);
                }
            } catch (e) { /* silent */ }
        };
        check();
        const i = setInterval(check, 30000);
        return () => { cancelled = true; clearInterval(i); };
    }, [address, shown, fireConfetti, playLevelUpSound]);

    const acknowledge = async () => {
        if (!address || !unlock) return;
        try {
            await fetch(`${API}/api/social-sharing/acknowledge-unlock`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, tier_id: unlock.tier.id }),
            });
        } catch (e) { /* silent */ }
        setUnlock(null);
    };

    const mintNow = async () => {
        if (!address || !unlock) return;
        setMinting(true);
        try {
            const res = await fetch(`${API}/api/social-sharing/mint-badge`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address }),
            });
            const d = await res.json();
            if (d.success) {
                toast.success(`Badge ${d.badge?.tier_label} minté !`);
                fireConfetti(unlock.tier?.color || '#FFD700');
            } else {
                toast.error(d.error || 'Erreur mint');
            }
        } catch (e) { toast.error('Erreur réseau'); }
        await acknowledge();
        setMinting(false);
    };

    if (!unlock || !unlock.tier) return null;

    const t = unlock.tier;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn"
            data-testid="tier-up-modal"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="relative max-w-lg w-full rounded-2xl border-2 p-8 text-center"
                style={{
                    background: `radial-gradient(circle at 50% 0%, ${t.color}25, #0A0A0C 70%)`,
                    borderColor: `${t.color}80`,
                    boxShadow: `0 0 60px ${t.color}80, 0 0 120px ${t.color}40`,
                }}
            >
                <button
                    onClick={acknowledge}
                    aria-label="Fermer"
                    className="absolute top-3 right-3 text-white/40 hover:text-white p-1"
                    data-testid="tier-up-close-btn"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Big sparkly icon */}
                <div className="relative mx-auto mb-6 w-32 h-32 flex items-center justify-center">
                    <div
                        className="absolute inset-0 rounded-full animate-ping opacity-30"
                        style={{ backgroundColor: t.color }}
                    />
                    <div
                        className="relative w-28 h-28 rounded-full flex items-center justify-center border-4"
                        style={{ borderColor: t.color, backgroundColor: `${t.color}30` }}
                    >
                        <Award className="w-14 h-14" style={{ color: t.color }} />
                    </div>
                    <Sparkles className="absolute -top-2 -right-2 w-7 h-7 text-white animate-pulse" />
                    <Sparkles className="absolute -bottom-1 -left-2 w-5 h-5 text-white animate-pulse" />
                </div>

                <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Nouveau palier débloqué</div>
                <h2 className="text-5xl font-black mb-3" style={{ color: t.color }} data-testid="tier-up-label">
                    {t.label}
                </h2>
                <div className="text-2xl font-bold text-white mb-1">×{t.multiplier} B4P par conversion</div>
                <div className="text-sm text-white/60 mb-2">{t.perks}</div>
                <div className="text-xs text-white/40 mb-6">{unlock.total_conversions} conversions atteintes</div>

                <div className="flex flex-col sm:flex-row gap-3">
                    {unlock.already_minted ? (
                        <div className="flex-1 px-4 py-3 rounded-lg bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/30 font-bold text-sm">
                            <Sparkles className="w-4 h-4 inline mr-2" />Badge déjà minté
                        </div>
                    ) : (
                        <Button
                            onClick={mintNow}
                            disabled={minting}
                            className="flex-1 h-12 font-bold text-base shadow-lg"
                            style={{ backgroundColor: t.color, color: '#000' }}
                            data-testid="tier-up-mint-btn"
                        >
                            <Award className="w-5 h-5 mr-2" />
                            {minting ? 'Mint en cours...' : `Mint mon NFT ${t.label}`}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={acknowledge}
                        className="border-white/20 text-white/70 hover:bg-white/10"
                        data-testid="tier-up-later-btn"
                    >
                        Plus tard
                    </Button>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
                .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
            `}</style>
        </div>
    );
}
